/**
 * What the fill pill says (user, 20/10/2026: "be updated when hovering on
 * what it is actually doing, how many…"), pure: filling (what is being
 * written now, the counts, how long), waiting for the AI (a rate limit and
 * the next try), stalled (no heartbeat — the server restarted; Resume), done
 * with drafts that failed twice (Retry failed), or — 20/10/2026, the Belgium
 * test: a re-synced day had 21 new drafts and the pill said "1 draft without
 * texts" — not filling while drafts still have no texts (Fill now; the new
 * ones are said apart from the failed ones). Null = nothing to show.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenCreativeFillStatus } from '@trafficpulse/shared';

import { type ClockCtx, instantText } from '../../../utils/clock/dk-label';
import { inWords } from '../facts/window-now';

export type PillTone = 'filling' | 'waiting' | 'stalled' | 'failed';

export interface PillModel {
  tone: PillTone;
  title: string;
  /** "54/138". */
  count: string;
  /** The lines of the pop-up, the first one on hover. */
  lines: string[];
  action: 'resume' | 'retry' | 'fill' | null;
}

const n = (x: number) => x.toLocaleString('en-US');

function currentLine(s: DropGenCreativeFillStatus): string | null {
  const p = s.progress;
  if (!p?.current) return p ? 'Writing proven texts and fixed sender IDs — no AI call yet' : null;
  const c = p.current;
  const at = p.inFlight > 1 ? `, ${p.inFlight} at once` : '';
  return `Writing ${c.kind === 'texts' ? 'texts' : 'sender names'} for ${c.containerName}${c.groupName ? ` · ${c.groupName}` : ''} (unit ${c.index} of ${p.units.total}${at})`;
}

function countsLine(s: DropGenCreativeFillStatus): string {
  const parts = [`${n(s.filled)} filled`, s.failed > 0 ? `${n(s.failed)} failed` : '', `${n(s.pending)} to go`];
  return parts.filter(Boolean).join(' · ');
}

function sinceLine(s: DropGenCreativeFillStatus, now: Date, clock: ClockCtx): string | null {
  const startedAt = s.progress?.startedAt ?? null;
  if (!startedAt) return null;
  const min = Math.max(0, Math.round((now.getTime() - new Date(startedAt).getTime()) / 60_000));
  const pass = s.progress?.pass === 2 ? ' · second pass over the units that failed' : '';
  return `Started ${instantText(startedAt, clock)}, ${min === 0 ? 'just now' : `${inWords(min)} ago`}${pass}`;
}

export function pillModel(s: DropGenCreativeFillStatus | undefined, now: Date, clock: ClockCtx): PillModel | null {
  if (!s) return null;
  const count = `${n(s.filled)}/${n(s.total)}`;
  if (s.status === 'FILLING' && s.stalled) {
    const since = s.updatedAt ? ` since ${instantText(s.updatedAt, clock)}` : '';
    return { tone: 'stalled', title: 'Fill stalled', count, action: 'resume', lines: [`No progress${since} — the server restarted or the AI stopped answering`, 'It resumes by itself within two minutes, or press Resume', countsLine(s)] };
  }
  if (s.status === 'FILLING') return filling(s, now, clock, count);
  if (s.pending > 0) return notFilling(s, count);
  return null;
}

/** Drafts without texts while nothing is filling: the new ones (a re-synced day) apart from the ones that failed twice. */
function notFilling(s: DropGenCreativeFillStatus, count: string): PillModel {
  const failed = Math.min(s.failed, s.pending);
  const fresh = s.pending - failed;
  const lines = [
    fresh > 0 ? `${n(fresh)} new since the last fill — nothing has written their texts yet (a re-synced day adds drafts)` : null,
    failed > 0 ? `${n(failed)} failed after two tries — the Errors tab says why, per container and prompt group` : null,
    s.error,
    `${fresh > 0 ? 'Fill now' : 'Retry'} writes only the drafts still without texts, with the prompt groups as they are now`,
  ].filter((l): l is string => Boolean(l));
  return { tone: 'failed', title: `${n(s.pending)} draft${s.pending === 1 ? '' : 's'} without texts`, count, action: fresh > 0 ? 'fill' : 'retry', lines };
}

function filling(s: DropGenCreativeFillStatus, now: Date, clock: ClockCtx, count: string): PillModel {
  const wait = s.progress?.wait ?? null;
  const lines = [currentLine(s), countsLine(s), sinceLine(s, now, clock)].filter((l): l is string => l !== null);
  if (wait && new Date(wait.untilIso).getTime() > now.getTime()) {
    const secs = Math.max(1, Math.round((new Date(wait.untilIso).getTime() - now.getTime()) / 1000));
    return { tone: 'waiting', title: 'Waiting for the AI', count, action: null, lines: [`${wait.reason} — trying again in ${secs} s (try ${wait.attempt} of ${wait.of})`, ...lines] };
  }
  return { tone: 'filling', title: 'Filling texts', count, action: null, lines };
}
